import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TourCard from '../components/Tours/TourCard';
import {
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  BoltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { tourService } from '../services/tourService';
import { useTheme } from '../contexts/ThemeContext';
import PricingFilter from '../components/Common/PricingFilter';
import { applyFeaturedOrder } from '../utils/applyFeaturedOrder';

const AdventureTours: React.FC = () => {
  const { actualTheme } = useTheme();
  const [adventureTours, setAdventureTours] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get Adventure tours from the service
    const loadTours = async () => {
      try {
        setLoading(true);
        const tours = await tourService.getToursByCategory('Adventure');
        // Apply featured order
        const orderedTours = applyFeaturedOrder(tours);
        setAdventureTours(orderedTours);
      } catch (error) {
        console.error('Error loading Adventure tours:', error);
        setAdventureTours([]);
      } finally {
        setLoading(false);
      }
    };

    loadTours();
  }, []);

  // Listen for storage changes to update tours when admin makes changes (from other tabs only)
  useEffect(() => {
    let isHandling = false;
    const handleStorageChange = async (e: StorageEvent) => {
      // Only react to storage events from other tabs/windows, not our own
      if (e.key && (e.key.startsWith('tours_') || e.key === 'tours_cache') && !isHandling) {
        isHandling = true;
        try {
          setLoading(true);
          const tours = await tourService.getToursByCategory('Adventure');
          // Apply featured order
          const orderedTours = applyFeaturedOrder(tours);
          setAdventureTours(orderedTours);
        } catch (error) {
          console.error('Error loading Adventure tours on storage change:', error);
          setAdventureTours([]);
        } finally {
          setLoading(false);
          setTimeout(() => { isHandling = false; }, 1000); // Prevent rapid successive calls
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // No mock data - using only real admin data

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Floating Header Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 md:pt-12">
        <div className={`rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg ${actualTheme === 'dark'
          ? 'bg-gradient-to-br from-primary-900 to-primary-800 border border-primary-700/50'
          : 'bg-gradient-to-br from-primary-800 to-primary-900 border border-primary-700/30'
          } text-white`}>
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Adventure Tours
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-primary-100 max-w-2xl mx-auto leading-relaxed">
              Push your limits with thrilling adventure experiences across East Africa.
              From mountain peaks to white water rapids, discover your next adrenaline rush.
            </p>
          </div>
        </div>
      </div>

      {/* Global Pricing Filter */}
      <PricingFilter />

      {/* Features Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Our Adventure Tours?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our adventure experiences are designed to challenge you while ensuring safety
              and providing unforgettable memories in Africa's most spectacular settings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <BoltIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Adrenaline Rush</h3>
              <p className="text-neutral-600">Experience heart-pounding adventures designed for thrill seekers</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <ShieldCheckIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Safety First</h3>
              <p className="text-neutral-600">Professional guides and top-quality safety equipment for all activities</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MapPinIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Unique Locations</h3>
              <p className="text-neutral-600">Adventure in Africa's most spectacular and challenging landscapes</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Small Groups</h3>
              <p className="text-neutral-600">Intimate group sizes for personalized attention and safety</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tours Section */}
      <div className="py-16 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Adventure Experiences</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose from our carefully selected adventure experiences that offer
              authentic connections with local communities and traditions.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-800"></div>
              <span className="ml-4 text-xl text-neutral-600">Loading adventure tours...</span>
            </div>
          ) : adventureTours.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {adventureTours.map((tour) => (
                <TourCard
                  key={tour.id}
                  tour={tour}
                  showQuickBook={true}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-neutral-500 text-lg mb-4">
                No Adventure tours available at the moment.
              </div>
              <p className="text-neutral-400 mb-6">
                Check back soon for new adventure experiences, or explore our other tour categories.
              </p>
              <Link
                to="/tours"
                className="inline-flex items-center bg-primary-800 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                View All Tours
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Safety Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-primary-900 mb-4">Safety & Equipment</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Your safety is our top priority. All adventure activities are conducted
              with professional guides and certified safety equipment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-800 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Certified Guides</h3>
              <p className="text-neutral-600">All guides are professionally trained and certified for their respective activities</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-800 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Quality Equipment</h3>
              <p className="text-neutral-600">We use only certified, high-quality safety equipment for all adventure activities</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-800 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Emergency Support</h3>
              <p className="text-neutral-600">24/7 emergency support and medical assistance available for all activities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-16 bg-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready for Your Next Adventure?</h2>
          <p className="text-xl text-neutral-200 mb-8 max-w-3xl mx-auto">
            Challenge yourself with thrilling adventures across East Africa.
            From mountain peaks to white water rapids, your next adrenaline rush awaits.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/tours"
              className="bg-white text-primary-800 hover:bg-neutral-100 px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              View All Tours
            </Link>
            <Link
              to="/contact"
              className="bg-primary-700 hover:bg-primary-600 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdventureTours;